import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import React from "react";
import { useSwipeToClose } from "./hooks/useSwipeToClose";
import "./sass/Sass.scss";
import HomePage from "./pages/HomePage";
import Music from "./pages/Music";
import Skills from "./pages/Skills";
import Experience from "./pages/Timeline";
import Education from "./pages/Education";
import Resume from "./pages/Resume";
import Projects from "./pages/Projects";

const Portfolio = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Prevent body scroll when mobile menu is open
  React.useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add("menu-open");
    } else {
      document.body.classList.remove("menu-open");
    }

    return () => {
      document.body.classList.remove("menu-open");
    };
  }, [isMenuOpen]);

  // Enable swipe to close for mobile menu
  useSwipeToClose(isMenuOpen, () => setIsMenuOpen(false));

  // Handle escape key to close menu
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isMenuOpen]);

  const NavigationItem = ({ pageName, path, isMobile = false }) => (
    <Link
      to={path}
      className={`transition-colors duration-200 font-medium ${
        isMobile
          ? "mobile-menu-item"
          : "text-secondary-600 hover:text-primary-600"
      }`}
      onClick={() => setIsMenuOpen(false)}
    >
      {pageName}
    </Link>
  );

  const Navigation = () => (
    <nav className="bg-white/95 backdrop-blur-sm border-b border-secondary-200 sticky top-0 z-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">RS</span>
            </div>
            <span className="font-display font-semibold text-lg text-secondary-900">
              Rohit Saigal
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <NavigationItem pageName="Home" path="/" />
            <NavigationItem pageName="Experience" path="/experience" />
            <NavigationItem pageName="Skills" path="/skills" />
            <NavigationItem pageName="Projects" path="/projects" />
            <NavigationItem pageName="Resume" path="/resume" />
            <NavigationItem pageName="Music" path="/music" />
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="mobile-menu-button"
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                {isMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="md:hidden mobile-menu-backdrop z-backdrop"
                onClick={() => setIsMenuOpen(false)}
              />

              {/* Menu */}
              <motion.div
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="md:hidden fixed top-0 left-0 h-full w-80 max-w-[85vw] bg-white shadow-xl z-menu mobile-menu-container"
                style={{ backgroundColor: "white" }}
              >
                <div className="flex flex-col h-full bg-white">
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 border-b border-secondary-200">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">RS</span>
                      </div>
                      <span className="font-display font-semibold text-lg text-secondary-900">
                        Menu
                      </span>
                    </div>
                    <button
                      onClick={() => setIsMenuOpen(false)}
                      className="p-2 rounded-lg text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 transition-colors duration-200"
                      aria-label="Close menu"
                    >
                      <svg
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Menu Items */}
                  <div className="flex-1 px-4 py-6 space-y-2">
                    <NavigationItem pageName="Home" path="/" isMobile={true} />
                    <NavigationItem
                      pageName="Experience"
                      path="/experience"
                      isMobile={true}
                    />
                    <NavigationItem
                      pageName="Skills"
                      path="/skills"
                      isMobile={true}
                    />
                    <NavigationItem
                      pageName="Projects"
                      path="/projects"
                      isMobile={true}
                    />
                    <NavigationItem
                      pageName="Resume"
                      path="/resume"
                      isMobile={true}
                    />
                    <NavigationItem
                      pageName="Music"
                      path="/music"
                      isMobile={true}
                    />
                  </div>

                  {/* Footer */}
                  <div className="p-4 border-t border-secondary-200">
                    <div className="text-sm text-secondary-500 text-center">
                      <p>© 2024 Rohit Saigal</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );

  const Footer = () => (
    <footer className="bg-secondary-900 text-white py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
            <a
              href="mailto:rohitnsaigal@gmail.com"
              className="text-secondary-300 hover:text-white transition-colors duration-200"
            >
              rohitnsaigal@gmail.com
            </a>
            <a
              href="tel:4124772435"
              className="text-secondary-300 hover:text-white transition-colors duration-200"
            >
              412-477-2435
            </a>
          </div>
          <div className="text-sm text-secondary-400 text-center md:text-right">
            <p>© 2024 Rohit Saigal. All rights reserved.</p>
          </div>
        </div>
      </div>
    </footer>
  );

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-primary-50 flex flex-col">
        <Navigation />
        <main className="flex-1 relative z-content">
          <AnimatePresence mode="wait">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/experience" element={<Experience />} />
              <Route path="/education" element={<Education />} />
              <Route path="/skills" element={<Skills />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/resume" element={<Resume />} />
              <Route path="/music" element={<Music />} />
            </Routes>
          </AnimatePresence>
        </main>
        <Footer />
      </div>
    </Router>
  );
};

export default Portfolio;
